import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Loader2,
  Inbox,
  Trash2,
  X,
  Search,
  Mail,
  Phone,
  RefreshCw,
  Download,
  Star,
} from "lucide-react";
import { api, clearToken, getToken, formatApiError } from "../lib/api";
import { LOGO_URL } from "../data/content";

const FIELD_LABELS = {
  prenom: "Prénom",
  email: "Email",
  telephone: "Téléphone",
  pratique_chretienne: "Pratique en contexte chrétien",
  pourquoi_former: "Pourquoi se former",
  objectif_principal: "Objectif principal",
  motivation: "Motivation",
  temps_par_semaine: "Temps par semaine",
  description: "Se décrit comme",
  situation_pro: "Situation professionnelle",
  pret_investir: "Prêt à investir",
  budget_mensuel: "Budget mensuel",
  date_demarrage: "Date de démarrage",
  decision: "Décision (seul / famille)",
  autres_structures: "A contacté d'autres structures",
  domaines: "Domaines souhaités",
};

const ORDER = [
  "prenom",
  "email",
  "telephone",
  "pratique_chretienne",
  "pourquoi_former",
  "objectif_principal",
  "motivation",
  "temps_par_semaine",
  "description",
  "situation_pro",
  "pret_investir",
  "budget_mensuel",
  "date_demarrage",
  "decision",
  "autres_structures",
  "domaines",
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const ELIGIBLE_BUDGETS = [
  "185 - 220 € PAR MOIS durant 5 mois",
  "de 220 à 370€ PAR MOIS durant 3 ou 4 mois",
];
const isEligible = (a) => ELIGIBLE_BUDGETS.includes(a.budget_mensuel);

export default function AdminDashboard() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [qualifiedOnly, setQualifiedOnly] = useState(false);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    clearToken();
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/applications");
      setApps(data);
    } catch (e) {
      if (e.response?.status === 401) return logout();
      setError(formatApiError(e.response?.data?.detail, "Chargement impossible."));
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette candidature ?")) return;
    try {
      await api.delete(`/applications/${id}`);
      setApps((p) => p.filter((a) => a._id !== id));
      setSelected(null);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail, "Suppression impossible."));
    }
  };

  const filtered = apps
    .filter((a) => (qualifiedOnly ? isEligible(a) : true))
    .filter((a) => {
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return (
        (a.prenom || "").toLowerCase().includes(q) ||
        (a.email || "").toLowerCase().includes(q) ||
        (a.telephone || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => (isEligible(b) ? 1 : 0) - (isEligible(a) ? 1 : 0));

  const qualifiedCount = apps.filter(isEligible).length;

  const exportCsv = () => {
    const cols = [...ORDER, "created_at"];
    const headers = cols.map((c) => (c === "created_at" ? "Date" : FIELD_LABELS[c] || c));
    const escape = (v) => {
      const s = Array.isArray(v) ? v.join("; ") : v == null ? "" : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const rows = filtered.map((a) =>
      cols.map((c) => escape(c === "created_at" ? fmtDate(a[c]) : a[c])).join(",")
    );
    const csv = [headers.map(escape).join(","), ...rows].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `candidatures_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#0B0E17] text-[#FDFBF7]">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-[#D4AF37]/15 bg-[#0B0E17]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="h-10 w-10 object-contain" />
            <div>
              <p className="font-heading text-lg leading-tight">Candidatures</p>
              <p className="font-body text-xs uppercase tracking-[0.18em] text-[#C9A04E]">
                Espace administrateur
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCsv}
              disabled={apps.length === 0}
              data-testid="export-csv-button"
              className="flex items-center gap-2 rounded-full border border-[#D4AF37]/30 px-4 py-2 font-body text-sm text-[#FDFBF7] transition-colors hover:bg-[#D4AF37]/10 disabled:opacity-40"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button
              onClick={load}
              data-testid="refresh-button"
              className="flex items-center gap-2 rounded-full border border-[#D4AF37]/30 px-4 py-2 font-body text-sm text-[#FDFBF7] transition-colors hover:bg-[#D4AF37]/10"
            >
              <RefreshCw className="h-4 w-4" /> Actualiser
            </button>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-4 py-2 font-body text-sm font-semibold text-[#0B0E17] transition-colors hover:bg-[#e6c451]"
            >
              <LogOut className="h-4 w-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-heading text-3xl font-light">
              {apps.length} candidature{apps.length > 1 ? "s" : ""}
            </h1>
            <p className="font-body text-sm text-[#A1A9C3]">
              Dont <span className="text-[#D4AF37]">{qualifiedCount} qualifiée{qualifiedCount > 1 ? "s" : ""}</span>{" "}
              (budget éligible). Cliquez sur une carte pour voir le détail.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              onClick={() => setQualifiedOnly((v) => !v)}
              data-testid="filter-qualified-button"
              className={`flex items-center justify-center gap-2 rounded-full border px-4 py-2.5 font-body text-sm transition-colors ${
                qualifiedOnly
                  ? "border-[#D4AF37] bg-[#D4AF37] text-[#0B0E17]"
                  : "border-[#D4AF37]/30 text-[#FDFBF7] hover:bg-[#D4AF37]/10"
              }`}
            >
              <Star className="h-4 w-4" /> {qualifiedOnly ? "Tous" : "Qualifiés"}
            </button>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A1A9C3]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher (nom, email, tél.)"
                data-testid="search-input"
                className="w-full rounded-full border border-[#D4AF37]/20 bg-[#1A2033]/60 py-2.5 pl-10 pr-4 font-body text-sm text-[#FDFBF7] outline-none focus:border-[#D4AF37] sm:w-72"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-500/40 bg-red-500/10 px-5 py-4 font-body text-sm text-red-200">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#A1A9C3]">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#D4AF37]/20 py-24 text-center">
            <Inbox className="h-10 w-10 text-[#D4AF37]/50" />
            <p className="mt-4 font-heading text-xl">Aucune candidature</p>
            <p className="font-body text-sm text-[#A1A9C3]">
              Les nouvelles candidatures apparaîtront ici.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((a) => {
              const eligible = isEligible(a);
              return (
              <button
                key={a._id}
                onClick={() => setSelected(a)}
                data-testid={`app-card-${a._id}`}
                data-eligible={eligible}
                className={`glass group relative rounded-2xl p-6 text-left transition-transform hover:-translate-y-1 ${
                  eligible ? "ring-2 ring-[#D4AF37] bg-[#D4AF37]/[0.07]" : ""
                }`}
              >
                {eligible && (
                  <span
                    data-testid="qualified-badge"
                    className="absolute -top-2.5 left-5 inline-flex items-center gap-1 rounded-full bg-[#D4AF37] px-3 py-1 font-body text-[10px] font-bold uppercase tracking-wider text-[#0B0E17] shadow-lg"
                  >
                    <Star className="h-3 w-3 fill-current" /> Qualifié
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-xl text-[#FDFBF7]">{a.prenom}</h3>
                  <span className="rounded-full bg-[#D4AF37]/15 px-3 py-1 font-body text-[10px] uppercase tracking-wider text-[#D4AF37]">
                    {a.pret_investir}
                  </span>
                </div>
                <p className="mt-3 flex items-center gap-2 font-body text-sm text-[#A1A9C3]">
                  <Mail className="h-3.5 w-3.5" /> {a.email}
                </p>
                <p className="mt-1 flex items-center gap-2 font-body text-sm text-[#A1A9C3]">
                  <Phone className="h-3.5 w-3.5" /> {a.telephone}
                </p>
                {eligible && (
                  <p className="mt-2 font-body text-xs font-semibold text-[#D4AF37]">
                    {a.budget_mensuel}
                  </p>
                )}
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(a.domaines || []).slice(0, 4).map((d) => (
                    <span
                      key={d}
                      className="rounded-full border border-[#D4AF37]/20 px-2.5 py-0.5 font-body text-[11px] text-[#C9A04E]"
                    >
                      {d}
                    </span>
                  ))}
                </div>
                <p className="mt-4 font-body text-[11px] uppercase tracking-wider text-[#A1A9C3]/60">
                  {fmtDate(a.created_at)}
                </p>
              </button>
              );
            })}
          </div>
        )}
      </main>

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <div
            className="glass my-8 w-full max-w-2xl rounded-3xl p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="app-detail-modal"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="font-heading text-3xl font-light">{selected.prenom}</h2>
                  {isEligible(selected) && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#D4AF37] px-3 py-1 font-body text-[10px] font-bold uppercase tracking-wider text-[#0B0E17]">
                      <Star className="h-3 w-3 fill-current" /> Qualifié
                    </span>
                  )}
                </div>
                <p className="font-body text-sm text-[#A1A9C3]">{fmtDate(selected.created_at)}</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="close-modal-button"
                className="rounded-full border border-[#D4AF37]/30 p-2 text-[#A1A9C3] transition-colors hover:bg-[#D4AF37]/10"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 space-y-3">
              {ORDER.map((key) => {
                const val = selected[key];
                const display = Array.isArray(val) ? val.join(", ") : val;
                const highlight = key === "budget_mensuel" && isEligible(selected);
                return (
                  <div
                    key={key}
                    className={`rounded-xl border p-4 ${
                      highlight
                        ? "border-[#D4AF37] bg-[#D4AF37]/10"
                        : "border-[#D4AF37]/12 bg-[#0B0E17]/40"
                    }`}
                  >
                    <p className="font-body text-xs uppercase tracking-[0.14em] text-[#C9A04E]">
                      {FIELD_LABELS[key] || key}
                    </p>
                    <p className={`mt-1 font-body text-sm ${highlight ? "font-semibold text-[#D4AF37]" : "text-[#FDFBF7]"}`}>
                      {display || "—"}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <a
                href={`mailto:${selected.email}`}
                className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3 font-body text-sm font-semibold text-[#0B0E17] transition-colors hover:bg-[#e6c451]"
              >
                <Mail className="h-4 w-4" /> Répondre par email
              </a>
              <button
                onClick={() => remove(selected._id)}
                data-testid="delete-app-button"
                className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-5 py-3 font-body text-sm text-red-300 transition-colors hover:bg-red-500/10"
              >
                <Trash2 className="h-4 w-4" /> Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
